import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isReleaseGroupScope, type ReportsV3Params } from '../../../components/reportsv3/types';
import { useParams } from '../../../contexts/RouterContext';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { remove } from '../../../redux/api';
import ProjectGroupsModule from '../../../redux/project_grouping';

/**
 * Hook that returns a mutation to unpublish an SBOM report from the
 * organization's SBOM portal. Unpublishing is only supported at the release
 * group level. On success the release is refetched so the status updates
 * without a page reload.
 */
export function useUnpublishReport() {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const selectedReleaseId = useSelector(ProjectGroupsModule.selectors.getSelectedReleaseId);
  const dispatch = useDispatch();

  const releaseGroupIds = useMemo(() => {
    if (!isReleaseGroup) {
      return null;
    }
    const { projectGroupId, releaseId } = params;
    const resolvedReleaseId = releaseId ?? selectedReleaseId;
    if (resolvedReleaseId == null) {
      return null;
    }
    return {
      projectGroupId: Number(projectGroupId),
      releaseId: Number(resolvedReleaseId),
    };
  }, [isReleaseGroup, params, selectedReleaseId]);

  return useToastedMutation({
    mutationFn: () => {
      if (!releaseGroupIds) {
        return Promise.reject(new Error('Unpublish is only supported at release-group scope.'));
      }
      return remove(`/api/project_group/${releaseGroupIds.projectGroupId}/release/${releaseGroupIds.releaseId}/published-report`);
    },
    onSuccess: () => {
      dispatch(ProjectGroupsModule.actions.fetchRelease(releaseGroupIds!.projectGroupId, releaseGroupIds!.releaseId));
    },
    successToastMessage: 'Your SBOM has been unpublished from the portal.',
    errorToastMessage: 'Failed to unpublish SBOM from the portal',
  });
}
